/**
 * Org Context
 *
 * Provides the list of organizations and clients for agency / multi-tenant mode.
 *
 * Persistence:
 *   - The selected org slug is stored in localStorage under `adflow_selected_org`.
 *   - The selected client ID is stored under `adflow_selected_client`.
 *   - On next load the stored values are matched against fresh data.
 *     If the stored org or client no longer exists, the first available is selected.
 *
 * Circular-dependency note:
 *   This context does NOT import useAuth.  Instead it attempts to fetch orgs
 *   on mount and treats a network/401 failure as "not authenticated yet".
 *   The AccountProvider (which wraps OrgProvider in the component tree) already
 *   gates on isAuthenticated, so in practice the fetch only fires when the user
 *   has a valid token.
 *
 * Usage:
 *   import { useOrg } from '../context/OrgContext';
 *   const { orgs, selectedOrg, selectedOrgId, clients, selectedClient,
 *           selectedClientId, isAgencyMode, selectOrg, selectClient,
 *           isLoading, error } = useOrg();
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useAuth } from './AuthContext';
import { getUserOrgs, getOrgClients } from '../services/orgService';

const ORG_STORAGE_KEY = 'adflow_selected_org';
const CLIENT_STORAGE_KEY = 'adflow_selected_client';

const OrgContext = createContext(null);

export function OrgProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── Load orgs on mount ──────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) {
      setOrgs([]);
      setSelectedOrg(null);
      return;
    }

    let cancelled = false;

    async function loadOrgs() {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getUserOrgs();
        if (cancelled) return;

        const orgList = Array.isArray(data) ? data : [];
        setOrgs(orgList);

        // Restore persisted org selection, falling back to the first org
        const storedSlug = localStorage.getItem(ORG_STORAGE_KEY);
        const restored = storedSlug
          ? orgList.find((o) => o.slug === storedSlug)
          : null;

        setSelectedOrg(restored ?? orgList[0] ?? null);
      } catch (err) {
        if (!cancelled) {
          // Silently ignore 401/403 — user is likely not authenticated yet
          if (err?.status === 401 || err?.status === 403) {
            setOrgs([]);
            setSelectedOrg(null);
          } else {
            setError(err);
            setOrgs([]);
            setSelectedOrg(null);
          }
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    loadOrgs();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // ── Load clients whenever selectedOrg changes ──────────────────────
  useEffect(() => {
    if (!selectedOrg?.id) {
      setClients([]);
      setSelectedClient(null);
      return;
    }

    let cancelled = false;

    async function loadClients() {
      try {
        const data = await getOrgClients(selectedOrg.id);
        if (cancelled) return;

        const clientList = Array.isArray(data) ? data : [];
        setClients(clientList);

        // Restore persisted client selection, falling back to null ("All Clients")
        const storedClientId = localStorage.getItem(CLIENT_STORAGE_KEY);
        const restored = storedClientId
          ? clientList.find((c) => String(c.id) === storedClientId)
          : null;

        setSelectedClient(restored ?? null);
      } catch {
        if (!cancelled) {
          setClients([]);
          setSelectedClient(null);
        }
      }
    }

    loadClients();
    return () => { cancelled = true; };
  }, [selectedOrg?.id]);

  // ── Selection handlers ─────────────────────────────────────────────
  const selectOrg = useCallback((org) => {
    setSelectedOrg(org);
    if (org?.slug) {
      localStorage.setItem(ORG_STORAGE_KEY, org.slug);
    } else {
      localStorage.removeItem(ORG_STORAGE_KEY);
    }
    // Reset client selection when org changes
    setSelectedClient(null);
    localStorage.removeItem(CLIENT_STORAGE_KEY);
  }, []);

  const selectClient = useCallback((client) => {
    setSelectedClient(client);
    if (client?.id) {
      localStorage.setItem(CLIENT_STORAGE_KEY, String(client.id));
    } else {
      localStorage.removeItem(CLIENT_STORAGE_KEY);
    }
  }, []);

  const value = {
    orgs,
    selectedOrg,
    selectOrg,
    clients,
    selectedClient,
    selectClient,
    isAgencyMode: orgs.length > 0,
    isLoading,
    error,
    // Convenience IDs for API calls
    selectedOrgId: selectedOrg?.id ?? null,
    selectedClientId: selectedClient?.id ?? null,
  };

  return (
    <OrgContext.Provider value={value}>
      {children}
    </OrgContext.Provider>
  );
}

export function useOrg() {
  const ctx = useContext(OrgContext);
  if (!ctx) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return ctx;
}

export default OrgContext;
